import CurrencyCode from "../dto/CurrencyCodeEnum";
import ICustomerModel from "../model/applicationForm/ICustomer";
import IIncome from "@/module/main/dto/customer/IIncome";
import IEntrepreneur from "@/module/main/dto/customer/IEntrepreneur";
import Entrepreneur from "@/module/main/dto/customer/Entrepreneur";
import Address from "@/module/main/dto/customer/Address";
import IEmployee from "@/module/main/dto/customer/IEmployee";
import Employee from "@/module/main/dto/customer/Employee";
import ContractDuration from "@/module/main/dto/customer/ContractDuration";
import ContractDurationType from "@/module/main/dto/customer/ContractDurationTypeEnum";
import ContractDurationTypeEnum from "@/module/main/dto/customer/ContractDurationTypeEnum";
import FixedTerm from "@/module/main/dto/customer/FixedTerm";
import IAddressFlags from "@/module/main/dto/customer/IAddressFlags";
import AddressFlags from "@/module/main/dto/customer/AddressFlags";
import AddressFlagEnum from "@/module/main/dto/customer/AddressFlagEnum";
import AddressFlag from "@/module/main/dto/customer/AddressFlagEnum";
import IBankAccount from "@/module/main/dto/customer/IBankAccount";
import IIban from "@/module/main/dto/customer/IIban";
import INational from "@/module/main/dto/customer/INational";
import BankAccount from "@/module/main/dto/customer/BankAccount";
import BankAccountType from "@/module/main/dto/customer/BankAccountTypeEnum";
import Iban from "@/module/main/dto/customer/Iban";
import National from "@/module/main/dto/customer/National";
import IPrimaryPersonalDocument from "@/module/main/dto/customer/IPrimaryPersonalDocument";
import PrimaryPersonalDocument from "@/module/main/dto/customer/PrimaryPersonalDocument";
import IIdentificationDocument from "@/module/main/dto/customer/IIdentificationDocument";
import IdentificationDocument from "@/module/main/dto/customer/IdentificationDocument";
import SecondaryPersonalDocument from "@/module/main/dto/customer/SecondaryPersonalDocument";
import SecondaryDocumentType from "@/module/main/dto/customer/SecondaryDocumentTypeEnum";
import ISecondaryPersonalDocument from "@/module/main/dto/customer/ISecondaryPersonalDocument";
import IncomeType from "@/module/main/dto/customer/IncomeTypeEnum";
import PrimaryDocumentType from "@/module/main/dto/customer/PrimaryDocumentTypeEnum";
import YesNo from "@/app/service/YesNoEnum";
import Income from "@/module/main/dto/customer/Income";
import IContractDuration from "@/module/main/dto/customer/IContractDuration";
import IAddress from "@/module/main/dto/customer/IAddress";
import IAddressModel from "@/module/main/model/applicationForm/IAddress";
import BusinessArea from "@/module/main/dto/customer/BusinessAreaEnum";
import EnumUtil from "@/app/util/Enum";
import BaseMapperV2 from "@/module/main/service/BaseMapperV2";
import IContact from "@/module/main/dto/financing-v3/IContact";
import {Contact} from "@/module/main/dto/financing-v3/Contact";
import ContactTypeEnum from "@/module/main/dto/financing-v3/ContactTypeEnum";
import {Phone} from "@/module/main/dto/financing-v3/Phone";
import {Email} from "@/module/main/dto/financing-v3/Email";
import IAcademicIdentificationDocumentFull from "@/module/main/dto/customer/IAcademicIdentificationDocumentFull";
import AcademicIdentificationDocumentFull from "@/module/main/dto/customer/AcademicIdentificationDocumentFull";
import AcademicCardType from "@/module/main/dto/customer/AcademicCardTypeEnum";
import IConsent from "@/module/main/dto/financing-v3/IConsent";
import Consent from "@/module/main/dto/financing-v3/Consent";
import ConsentCode from "@/module/main/dto/financing-v3/ConsentCodeEnum";

class CustomerBaseMapper extends BaseMapperV2 {

    protected getPoliticallyExposed(customer: ICustomerModel): boolean | undefined {
        let yesNoValue: YesNo | undefined = EnumUtil.getEnumValue(YesNo, customer.block3Others.politicallyExposed);
        let ret: boolean | undefined;

        switch (yesNoValue) {
            case YesNo.YES:
                ret = true;
                break;
            case YesNo.NO:
                ret = false;
                break;
            default:
                ret = undefined;
        }

        return ret;
    }

    protected getSecondaryPersonalDocument(customer: ICustomerModel): ISecondaryPersonalDocument | undefined {
        let type: SecondaryDocumentType | undefined = EnumUtil.getEnumValue(
            SecondaryDocumentType,
            customer.block3Others.secondaryDocumentType
        );
        let birthCertificate!: IIdentificationDocument;
        let drivingLicense!: IIdentificationDocument;
        let gunLicense!: IIdentificationDocument;
        let insuranceCard!: IIdentificationDocument;
        let passport!: IIdentificationDocument;
        let academic!: IAcademicIdentificationDocumentFull;

        switch (type) {
            case SecondaryDocumentType.BIRTH_CERTIFICATE:
                birthCertificate = new IdentificationDocument(
                    customer.block3Others.secondaryDocumentNumber || undefined
                );
                break;
            case SecondaryDocumentType.DRIVING_LICENSE:
                drivingLicense = new IdentificationDocument(customer.block3Others.secondaryDocumentNumber || undefined);
                break;
            case SecondaryDocumentType.GUN_LICENSE:
                gunLicense = new IdentificationDocument(customer.block3Others.secondaryDocumentNumber || undefined);
                break;
            case SecondaryDocumentType.INSURANCE_CARD:
                insuranceCard = new IdentificationDocument(customer.block3Others.secondaryDocumentNumber || undefined);
                break;
            case SecondaryDocumentType.PASSPORT:
                passport = new IdentificationDocument(customer.block3Others.secondaryDocumentNumber || undefined);
                break;
            case SecondaryDocumentType.ACADEMIC_CARD:
                academic = new AcademicIdentificationDocumentFull(customer.block3Others.secondaryDocumentNumber || undefined,
                    undefined, customer.block3Others.academicCardFirstName || undefined, customer.block3Others.academicCardLastName || undefined,
                    EnumUtil.getEnumValue(AcademicCardType, customer.block3Others.academicCardType) || undefined);
                break;
            default:
                return undefined;
        }

        return new SecondaryPersonalDocument(
            type,
            birthCertificate,
            drivingLicense,
            gunLicense,
            insuranceCard,
            passport,
            academic
        );
    }

    protected getPrimaryPersonalDocument(customer: ICustomerModel): IPrimaryPersonalDocument | undefined {
        let type: PrimaryDocumentType | undefined = EnumUtil.getEnumValue(
            PrimaryDocumentType,
            customer.block2Others.primaryDocumentType
        );
        let identityCard!: IIdentificationDocument;
        let residencePermit!: IIdentificationDocument;

        switch (type) {
            case PrimaryDocumentType.IDENTITY_CARD:
                identityCard = new IdentificationDocument(
                    customer.block2Others.primaryDocumentNumber || undefined,
                    this.formatDate(customer.block2Others.primaryDocumentValidity)
                );
                break;
            case PrimaryDocumentType.RESIDENCE_PERMIT:
                residencePermit = new IdentificationDocument(
                    customer.block2Others.primaryDocumentNumber || undefined,
                    this.formatDate(customer.block2Others.primaryDocumentValidity)
                );
                break;
            default:
                return undefined;
        }

        return new PrimaryPersonalDocument(type, identityCard, residencePermit);
    }

    protected getBankAccount(customer: ICustomerModel, currencyCode: CurrencyCode): IBankAccount | undefined {
        let type: BankAccountType;
        let national: INational | undefined = undefined;
        let iban: IIban | undefined = undefined;

        if (currencyCode == CurrencyCode.EUR) {
            if (!customer.block3Others.iban) {
                return undefined;
            }

            type = BankAccountType.IBAN;
            iban = new Iban(customer.block3Others.iban || undefined);
        } else {
            if (!customer.block3Others.prefix &&
                !customer.block3Others.accountNumber &&
                !customer.block3Others.bankCode) {
                return undefined;
            }

            type = BankAccountType.NATIONAL;
            national = new National(
                customer.block3Others.prefix || undefined,
                customer.block3Others.accountNumber || undefined,
                customer.block3Others.bankCode || undefined
            );
        }

        return new BankAccount(type, national, iban);
    }

    protected getAddresses(customer: ICustomerModel): IAddressFlags[] | undefined {
        let addresses: IAddressFlags[] = [];

        if (customer.block3Address.contactSameAsPermanent === "true") {
            this.addAddress(addresses, customer.block3Address.contactAddress, [
                AddressFlagEnum.CONTACT,
                AddressFlagEnum.PERMANENT
            ]);
        } else {
            this.addAddress(addresses, customer.block3Address.contactAddress, [AddressFlagEnum.CONTACT]);
            this.addAddress(addresses, customer.block3Address.permanentAddress, [AddressFlagEnum.PERMANENT]);
        }

        return addresses.length > 0 ? addresses : undefined;
    }

    protected addAddress(addresses: IAddressFlags[], modelAddress: IAddressModel, flags: AddressFlag[]): void {
        let address: IAddressFlags | undefined = this.getAddressFlag(modelAddress);
        if (address) {
            address.flags = flags;
            addresses.push(address);
        }
    }

    private getAddressFlag(modelAddress: IAddressModel): IAddressFlags | undefined {
        let address: IAddress | undefined = this.getAddress(modelAddress);

        return address
            ? new AddressFlags(address.streetAddress, address.streetNumber, address.city, address.zip, address.country)
            : undefined;
    }

    private getAddress(modelAddress: IAddressModel): IAddress | undefined
    {
        let address: IAddress | undefined = undefined;

        if (modelAddress.streetAddress ||
            modelAddress.streetNumber ||
            modelAddress.city ||
            modelAddress.zip ||
            modelAddress.country)
        {
            address = new Address(modelAddress.streetAddress || undefined,
                modelAddress.streetNumber || undefined,
                modelAddress.city || undefined,
                modelAddress.zip || undefined,
                modelAddress.country || undefined
            );
        }

        return address;
    }

    protected getIncome(customer: ICustomerModel, currencyCode: CurrencyCode): IIncome | undefined
    {
        let type: IncomeType | undefined = EnumUtil.getEnumValue(IncomeType, customer.block2Income.type);
        let entrepreneur: IEntrepreneur | undefined = undefined;
        let employee: IEmployee | undefined = undefined;

        switch (type)
        {
            case IncomeType.ENTREPRENEUR:
                entrepreneur = this.getEntrepreneur(customer)
                break;
            case IncomeType.EMPLOYEE:
                employee = this.getEmployee(customer);
                break;
        }

        return !!type
            ? new Income(type,
                this.createAmount(customer.block2Income.income, currencyCode),
                employee,
                entrepreneur)
            : undefined;
    }

    private getEntrepreneur(customer: ICustomerModel): IEntrepreneur | undefined {
        let address: IAddress | undefined = this.getAddress(customer.block3Entrepreneur.address);
        let addresses: [IAddress] | undefined = address ? [address] : undefined;

        return new Entrepreneur(
            this.formatDate(customer.block2Income.employedFrom),
            addresses,
            EnumUtil.getEnumValue(BusinessArea, customer.block3Entrepreneur.businessArea),
            customer.block3Entrepreneur.businessId || undefined,
            customer.block3Entrepreneur.businessName || undefined
        );
    }

    private getEmployee(customer: ICustomerModel): IEmployee | undefined {

        return new Employee(
            this.getContractDuration(customer),
            this.getEmployerAddresses(customer),
            customer.block3Employee.employerName || undefined,
            customer.block3Employee.businessId || undefined
        );
    }

    private getEmployerAddresses(customer: ICustomerModel): [IAddress] | undefined {
        let employerAddresses: [IAddress] | undefined = undefined;

        if (customer.block3Employee.address.streetAddress ||
            customer.block3Employee.address.streetNumber ||
            customer.block3Employee.address.city ||
            customer.block3Employee.address.zip ||
            customer.block3Employee.address.country) {
            employerAddresses = [
                new Address(
                    customer.block3Employee.address.streetAddress || undefined,
                    customer.block3Employee.address.streetNumber || undefined,
                    customer.block3Employee.address.city || undefined,
                    customer.block3Employee.address.zip || undefined,
                    customer.block3Employee.address.country || undefined
                )
            ];
        }

        return employerAddresses;
    }

    private getContractDuration(customer: ICustomerModel): IContractDuration | undefined {
        let contractDurationType: ContractDurationType | undefined = EnumUtil.getEnumValue(
            ContractDurationType,
            customer.block3Employee.contractDurationType
        );
        let contractDuration: IContractDuration | undefined;

        switch (contractDurationType) {
            case ContractDurationType.PERMANENT:
                contractDuration = new ContractDuration(ContractDurationType.PERMANENT);
                break;
            case ContractDurationTypeEnum.FIXED_TERM:
                contractDuration = new ContractDuration(
                    ContractDurationType.FIXED_TERM,
                    new FixedTerm(this.formatDate(customer.block3Employee.contractDurationValidity))
                );
                break;
            default:
                contractDuration = undefined;
        }

        if (!!customer.block2Income.employedFrom) {
            if (contractDuration == undefined) {
                contractDuration = new ContractDuration();
            }
            contractDuration.employedFrom = this.formatDate(customer.block2Income.employedFrom);
        }

        return contractDuration;
    }

    protected getContacts(phone: string | undefined,  email: string | undefined): IContact[] | undefined
    {
        let contacts: IContact[] = [];

        if (!!phone)
        {
            contacts.push(new Contact({ contactType: ContactTypeEnum.MOBILE, mobile: new Phone(phone) }))
        }

        if (!!email)
        {
            contacts.push(new Contact({ contactType: ContactTypeEnum.EMAIL, email: new Email(email) }))
        }

        return contacts.length > 0 ? contacts : undefined;
    }

    protected getConsents(customer: ICustomerModel): IConsent[] | undefined
    {
        let consents: IConsent[] = [];

        if (!!customer.block1.consentFinancialDispo)
        {
            consents.push(new Consent({
                code: ConsentCode.FINANCIAL_DISPO,
                granted: customer.block1.consentFinancialDispo === YesNo.YES
            }));
        }

        return consents.length > 0 ? consents : undefined;
    }
}

export default CustomerBaseMapper;
